/**
 * The course funnel, recorded in two places at once.
 *
 * `track()` sends to the ad platforms, which is what bidding and remarketing
 * need. That copy is consent-gated and vendor-shaped: it goes to GA4, Meta and
 * TikTok, it disappears entirely for a visitor who declines, and none of it can
 * be queried from our own admin panel.
 *
 * So every step is also posted to `/api/conversion/track`, which is ours. That
 * copy is what the conversion dashboard reads, what joins a page view to a
 * settled payment, and what survives a visitor saying no to advertising.
 *
 * ## Why the first-party copy is not consent-gated
 *
 * It carries no personal data. A course slug and the campaign labels the
 * business put on its own links are not information about a person — they are
 * information about our own marketing, which happens to have been observed on a
 * page. The same reasoning already governs `attribution.ts`, which writes to
 * first-party storage regardless of consent for the same reason.
 *
 * The one field that could be argued either way is `anonId`, which is a random
 * per-browser string. It is therefore the one field that *is* gated: without
 * analytics consent the event is still recorded, just without anything joining
 * it to that browser's other events.
 *
 * Nothing here ever sends an email address, a phone number, a name, or anything
 * a card was charged with.
 */

import { track, type AnalyticsEvent, type EventParams } from './analytics';
import { getAttributionForEvent, getFirstTouch, getLastTouch } from './attribution';
import { isAllowed } from './consent';
import { postFunnelEvent, type FunnelEventType } from '../apis/conversion';

/**
 * The two vocabularies, mapped once.
 *
 * The vendor names are product-facing and read as verbs a marketer recognises;
 * the server names are the funnel steps the dashboard computes rates between.
 * They are not the same list — `payment_confirmed` exists only on the server
 * side, because a browser is not allowed to assert that money arrived.
 */
const SERVER_NAME: Partial<Record<AnalyticsEvent, FunnelEventType>> = {
  /**
   * The lead stage, added when paid traffic arrived.
   *
   * This whole half of the funnel used to exist only in the vendor pixels —
   * which this file's own opening comment argues are unreliable, because a
   * third of visitors decline the consent they depend on. Cost per lead was
   * therefore uncomputable from our own data: the lead tables know how many
   * leads exist, and nothing knew how many people saw the page that asks for
   * one.
   *
   * `lead_magnet_success` is deliberately absent. The server writes
   * `lead_created` itself once the contact row actually exists — a browser
   * asserting "a lead was created" would be asserting the denominator of every
   * cost-per-lead figure the advertising budget is judged on.
   */
  lead_magnet_view: 'landing_page_view',
  campaign_page_view: 'landing_page_view',
  lead_magnet_form_start: 'lead_form_start',
  lead_magnet_submit: 'lead_form_submit',
  lead_magnet_download: 'lead_magnet_download',
  course_sales_page_view: 'sales_page_view',
  /**
   * The two contact routes off a course sales page.
   *
   * Recorded before the hop, because there is no other moment to record them
   * in: both hand the visitor to another application, and nothing on this page
   * ever hears what happened next. They are therefore intents and are named as
   * clicks, not as joins — the Telegram bot writes the actual join back onto
   * the lead when the deep link is opened, and the gap between the two counts
   * is a real number worth reading rather than an error.
   */
  whatsapp_chat_click: 'whatsapp_contact_click',
  telegram_join_click: 'telegram_contact_click',
  /**
   * The short contact form on the same surface, sharing the lead-stage steps
   * with the free-resource form rather than inventing parallel ones: a lead
   * form was started and a lead form was submitted, and where it stood on the
   * page is `courseSlug` and the attribution, not a different vocabulary.
   *
   * `course_contact_success` is deliberately absent, exactly as
   * `lead_magnet_success` is: the server writes `lead_created` once the row
   * exists.
   */
  course_contact_form_start: 'lead_form_start',
  course_contact_submit: 'lead_form_submit',
  course_syllabus_expand: 'syllabus_expand',
  course_preview_interaction: 'preview_interaction',
  course_enrol_click: 'enrol_click',
  add_to_cart: 'cart_add',
  promo_code_apply: 'promo_code_apply',
  payment_failed: 'payment_failed',
};

/**
 * `begin_checkout` is deliberately absent from that map.
 *
 * The server writes `checkout_start` itself inside `openCheckout`, at the
 * moment it prices the basket and mints the reference — which is both more
 * truthful (it means "an order really was opened", not "a button was pressed")
 * and better attributed, because that is where the browser's campaign
 * parameters are merged with what the lead table knows.
 *
 * Mapping it here as well would post a second row for the same event and
 * quietly double every checkout figure on the dashboard, which would in turn
 * halve the reported checkout-to-payment rate. The vendor copy still fires —
 * `trackFunnel` always calls `track()` — so Meta and TikTok get their
 * `InitiateCheckout` exactly once.
 *
 * The same reasoning applies to `payment_initiated`, `payment_confirmed`,
 * `enrolment_granted` and `first_lesson_started`: all server-written, none
 * mapped.
 *
 * `complete_registration` is absent for the same reason and must stay absent.
 * It still fires from `pages/Auth.tsx` on both signup paths, because Meta needs
 * it to optimise — but the first-party copy, `account_created`, is written by
 * `recordRegistration` at the moment the user row exists, on all three doors
 * into the platform including the guest checkout that never touches this file.
 * Mapping it here would post a second row from a browser that cannot know
 * whether the account was really created, and would count a Google sign-in as a
 * registration. The tracking endpoint refuses the type outright; this note is
 * why nobody should try.
 */

const ANON_KEY = 'mdx-anon-id';

/**
 * A random id for this browser, minted on first use.
 *
 * Not a fingerprint: nothing about the device or the network goes into it, it
 * is a random string in this site's own storage, and clearing site data
 * destroys it. Its entire job is to let "someone viewed the SPSS page" and
 * "someone bought SPSS forty minutes later" be recognised as one journey.
 */
export const getAnonId = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  if (!isAllowed('analytics')) return undefined;

  try {
    const existing = window.localStorage.getItem(ANON_KEY);
    if (existing) return existing;

    const minted =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `a_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

    window.localStorage.setItem(ANON_KEY, minted);
    return minted;
  } catch {
    // Storage disabled. Events still record; they simply cannot be joined.
    return undefined;
  }
};

export interface FunnelContext {
  courseSlug?: string;
  courseTitle?: string;
  courseId?: string;
  value?: number;
  currency?: string;
  promoCode?: string;
  /** Anything else the vendor copy should carry. Never personal data. */
  extra?: EventParams;
}

/**
 * The attribution to attach to a server-side record.
 *
 * Richer than `getAttributionForEvent`, which is shaped for a vendor payload:
 * this one also carries the referrer and landing page, and names the fields the
 * way the funnel collection stores them, so the lead table and the funnel can
 * be joined on the same keys.
 */
export const funnelAttribution = (): Record<string, string> => {
  const last = getLastTouch();
  const first = getFirstTouch();
  const source = Object.keys(last).length > 0 ? last : first;

  const out: Record<string, string> = {};
  if (source.utm_source) out.source = source.utm_source;
  if (source.utm_medium) out.medium = source.utm_medium;
  if (source.utm_campaign) out.campaign = source.utm_campaign;
  if (source.utm_content) out.content = source.utm_content;
  if (source.utm_term) out.term = source.utm_term;
  if (source.referrer) out.referrer = source.referrer;
  if (source.landing_page) out.landingPage = source.landing_page;

  /**
   * The advertisement's own identifiers.
   *
   * Carried because spend is exported keyed on them while conversions arrive
   * keyed on whatever label somebody typed into the ad builder. Without these,
   * cost per lead can only be computed at whatever granularity the naming
   * convention happened to survive — which is campaign level on a good day and
   * nothing at all when two ads share a name.
   *
   * Not click identifiers: those go to `/api/attribution/touch` and nowhere
   * else. See `lib/touch.ts`.
   */
  if (source.campaign_id) out.campaignId = source.campaign_id;
  if (source.adset_id) out.adSetId = source.adset_id;
  if (source.ad_id) out.adId = source.ad_id;
  if (source.creative_id) out.creativeId = source.creative_id;

  /**
   * Which free resource started this, if any.
   *
   * The `/free/<slug>` pages land people with `utm_content` naming the pack,
   * and the thank-you page links on to the course. Reading it off the landing
   * page as well is what keeps the bridge intact for somebody who arrived
   * organically, downloaded a pack, and came back a week later — the server
   * fills the rest from the lead table at checkout.
   */
  const fromLanding = source.landing_page?.match(/^\/free\/([a-z0-9-]+)/i)?.[1];
  if (fromLanding) out.leadMagnet = fromLanding;

  return out;
};

/**
 * Record one step of the funnel.
 *
 * The vendor send is synchronous and de-duplicated by the analytics layer; the
 * first-party post is fire-and-forget and never surfaces an error, because a
 * measurement failing must not interrupt someone trying to buy something.
 */
export const trackFunnel = (event: AnalyticsEvent, context: FunnelContext = {}): void => {
  const { courseSlug, courseTitle, courseId, value, currency, promoCode, extra } = context;

  track(event, {
    course_slug: courseSlug,
    course_title: courseTitle,
    course_id: courseId,
    value,
    currency: currency ?? (value !== undefined ? 'NGN' : undefined),
    promo_code: promoCode,
    ...extra,
  });

  const serverName = SERVER_NAME[event];
  if (!serverName) return;

  void postFunnelEvent({
    type: serverName,
    courseSlug,
    anonId: getAnonId(),
    attribution: funnelAttribution(),
    promoCode,
  });
};

/**
 * The campaign parameters to hand to the server when opening a checkout.
 *
 * Same shape as the funnel attribution, plus whatever the vendor helper knows,
 * so the `CheckoutAttempt` row and the funnel rows agree about where this
 * order came from.
 */
export const checkoutAttribution = (): Record<string, string> => ({
  ...getAttributionForEvent(),
  ...funnelAttribution(),
});
